import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import hoistNonReactStatic from 'hoist-non-react-statics'
import { useDidUpdate } from 'react-nb-hooks'
import { destroy as destroyClientSearch } from './shared/ClientSearch'
import { destroy as destroyApplicationSearch } from './shared/ApplicationSearch'
import { unsl_instance, zone_instance } from './AMLClient/constants'
import { instanceName as clientSearchInstanceName } from './ClientSearchGeneral/constants'
import { instanceName as applicationSearchInstanceName } from './ApplicationSearchGeneral/constants'
import { destroy as destroyLoanSearch } from './LoanSearch/actions'
import { destroy as destroyCollateralSearch } from './CollateralSearch'
import { destroy as destroyAccountSearch } from './AccountSearch'
import { destroy as destroyOrderSearch } from './OrderSearch/actions'
import OpenedWindows from './OpenedWindows'
import * as openedPagesActions from '../store/persist/openedPages.actions'

const OpenPagesContext = React.createContext({
  count: 0,
  onRemoveWindowIfExists: (path) => {},
  onRemoveWindow: (params) => {},
  onAddWindow: (params) => {},
})

function getRedirectUrl(order, uri, tabsSlider) {
  const currentUriIndex = order.indexOf(uri)

  const redirectUri = order[currentUriIndex - 1] || order[currentUriIndex + 1] || null

  const type =
    redirectUri && redirectUri.split('/')[2]
      ? redirectUri.split('/')[2].charAt(0).toUpperCase() + redirectUri.split('/')[2].slice(1)
      : undefined

  const searchID = redirectUri && redirectUri.split('/')[3] ? redirectUri.split('/')[3] : undefined

  const tabs = tabsSlider ? tabsSlider[`${type}-${searchID}-Details`] : undefined

  if (tabs && redirectUri) {
    const activeTabID = tabs.list.findIndex((item) => item.name === tabs.activeTab)

    const nextLink = `${redirectUri}?tab=${parseFloat(activeTabID) + parseFloat(1)}`
    return nextLink
  }

  return order[currentUriIndex - 1] || order[currentUriIndex + 1] || null
}

const getRedirectUrlCurry = (tabsSlider) => (order, uri) => getRedirectUrl(order, uri, tabsSlider)

const OpenPagesProvider = ({ children }) => {
  const dispatch = useDispatch()

  const history = useHistory()

  const DetailsContainerData = useSelector((state) => state.DetailsContainer)
  const tabsSlider = useSelector((state) => state.TabSlider)
  const openedPagesState = useSelector((state) => state.persisted.openedPages)

  useDidUpdate(() => {
    history.push((openedPagesState.activeItem || '') + (openedPagesState.search || '') || '/secure')
  }, [openedPagesState.activeItem])

  function onRemoveWindow({ id, nextID }) {
    // nextActiveItem will be item or function
    const nextActiveItem = nextID || getRedirectUrlCurry(tabsSlider)
    dispatch(openedPagesActions.removeItem({ id, nextActiveItem }))

    switch (id) {
      case '/secure/client/searchGeneral':
        dispatch(destroyClientSearch(clientSearchInstanceName))
        break
      case '/secure/client/risks':
        dispatch(destroyClientSearch(unsl_instance))
        dispatch(destroyClientSearch(zone_instance))
        break
      case '/secure/application/search':
        destroyApplicationSearch(applicationSearchInstanceName, dispatch)
        break
      case '/secure/loan/search':
        dispatch(destroyLoanSearch())
        break
      case '/secure/collateral/search':
        dispatch(destroyCollateralSearch())
        break
      case '/secure/account/search':
        dispatch(destroyAccountSearch())
        break
      case '/secure/order/search':
        dispatch(destroyOrderSearch())
        break
      default:
        break
    }
  }

  function onRemoveWindowIfExists(possibleUrlPrefix) {
    dispatch(openedPagesActions.removeItem({ id: possibleUrlPrefix, nextActiveItem: getRedirectUrlCurry(tabsSlider) }))
  }

  return (
    <OpenedPagesBase
      openedPagesState={openedPagesState}
      onAddWindow={(payload) => dispatch(openedPagesActions.add(payload))}
      onRemoveWindow={onRemoveWindow}
      onRemoveWindowIfExists={onRemoveWindowIfExists}
      onRemoveActive={() => dispatch(openedPagesActions.removeActive(getRedirectUrlCurry(tabsSlider)))}
      onSelectWindow={(tab) => dispatch(openedPagesActions.select(tab.id))}
      onReset={() => {
        dispatch({ type: 'openedPages/RESET' })
        history.push('/secure')
      }}
      tabsSlider={tabsSlider}
      DetailsContainerData={DetailsContainerData}
      children={children} // eslint-disable-line
    />
  )
}

class OpenedPagesBase extends React.Component {
  static getDerivedStateFromError(error) {
    console.error(error)
    return { hasError: true }
  }

  componentDidCatch(error) {
    console.error(error)
    this.props.onReset()
  }

  render() {
    const {
      openedPagesState,
      onAddWindow,
      onRemoveWindow,
      onRemoveWindowIfExists,
      onRemoveActive,
      onSelectWindow,
      tabsSlider,
      DetailsContainerData,
      children,
    } = this.props

    return (
      <OpenPagesContext.Provider
        value={{
          onAddWindow,
          onRemoveWindow,
          onRemoveWindowIfExists,
          onRemoveActive,
          onSelectWindow,
          redirectUrl: getRedirectUrl(openedPagesState.order, openedPagesState.activeItem, tabsSlider),
          count: openedPagesState.order.length,
        }}
      >
        {children}
        <OpenedWindows
          activeWindow={openedPagesState.activeItem}
          list={openedPagesState.list}
          order={openedPagesState.order}
          DetailsContainerData={DetailsContainerData}
          tabsSlider={tabsSlider}
          onRemoveWindowRequest={onRemoveWindow}
        />
      </OpenPagesContext.Provider>
    )
  }
}

function useOpenedPages() {
  return React.useContext(OpenPagesContext)
}

const withOpenedPages = (Component) => {
  const EnhancedComponent = (props) => {
    const value = useOpenedPages()
    return <Component {...props} openedPages={value} />
  }

  hoistNonReactStatic(EnhancedComponent, Component)

  return EnhancedComponent
}

const getOpenedPages = (state) => state.persisted.openedPages.list

export { OpenPagesProvider, useOpenedPages, withOpenedPages, getOpenedPages }
